import { IMsalContext } from "@azure/msal-react";
import { loginRequest } from "../configurations/authConfig";
import { IPublicClientApplication, AccountInfo, AuthenticationResult, InteractionType, SsoSilentRequest, InteractionStatus, EndSessionPopupRequest, EndSessionRequest } from "@azure/msal-browser";
import { SilentRequest } from "@azure/msal-browser/dist/request/SilentRequest";
import * as _httpService from "./HttpService";
import { AxiosHeaders, AxiosRequestConfig, AxiosRequestHeaders, AxiosResponse } from "axios";
import ApiEndpoints, { IUserControllerEndpoint } from "../configurations/ApiEndpoints";
import { useNavigate } from "react-router-dom";
import { CustomNavigationClient } from "../Helpers/helper";

const DefaultEndpoint: string = "/";

const ApiPath = (): IUserControllerEndpoint => {
    const baseEndoint: IUserControllerEndpoint | undefined = new ApiEndpoints().User;

    if (!baseEndoint)
        throw new Error("Menu endpoint could not be null.");

    return baseEndoint;
}

export interface ISsoService {
    readonly Ready: boolean;
    Login: (type: InteractionType) => void;
    LoginCallBack: (type: InteractionType, callback: (response?: AuthenticationResult) => void) => void;
    Logout: (type: InteractionType, afterCallback?: () => void) => void;
    GetProfileData: (callback: (response: AxiosResponse<any, any>) => void) => void;
    GetMSGraphData: (Callback: (response: AxiosResponse<any, any>) => void) => void;
    GetAccountInfo: () => AccountInfo | null;
    GetToken: (updateAdToken?: (token?: string) => void, errorCallback?: (error?: unknown) => void) => void;
    GetAuthentication: (successCallback?: (auth: AuthenticationResult) => void, errorCallback?: (error?: unknown) => void) => void;
    Authenticate: (setAuth: React.Dispatch<React.SetStateAction<AuthenticationResult | null>>) => void
    Navigate: () => void;
    SSO: () => Promise<AuthenticationResult>;
}

export default class SsoService implements ISsoService {

    readonly Ready: boolean;

    private readonly _ctx: IMsalContext;
    private readonly _client: IPublicClientApplication;

    constructor(context: IMsalContext) {

        if (!context)
            throw new Error("Context could not be null or empty.");
        this._ctx = context;
        this._client = this._ctx.instance;
        this.Ready = this._ctx.inProgress == InteractionStatus.None;
    }

    Login = (type: InteractionType): void => {
        switch (type) {
            case InteractionType.Popup:
                this._client.loginPopup(loginRequest)
                    .then(response => {
                        this._client.setActiveAccount(response.account)
                    })
                    .catch(ex => console.error(ex));
                break;
            case InteractionType.Redirect:
                this._client.loginRedirect(loginRequest);
                break;
            default:
                throw new Error("Login type not implemented yet.");
        }
    }

    LoginCallBack = (type: InteractionType, callback: (response?: AuthenticationResult) => void): void => {
        const promise = type == InteractionType.Popup ?
            this._client.loginPopup(loginRequest) :
            this._client.loginRedirect(loginRequest);

        promise.then(response => {
            (type == InteractionType.Popup) ?
                callback && callback(response as AuthenticationResult) :
                callback && callback();
        })
            .catch(ex => console.error(ex))
    }

    Logout = (type: InteractionType, afterCallback?: () => void): void => {


        const request = type == InteractionType.Popup ?
            {//Popup
                postLogoutRedirectUri: DefaultEndpoint,
                mainWindowRedirectUri: DefaultEndpoint
            } as EndSessionPopupRequest :
            {//Redirect
                postLogoutRedirectUri: DefaultEndpoint
            } as EndSessionRequest,

            execution = new Promise<void>((Resolve) => {
                type == InteractionType.Popup && Resolve(this._client.logoutPopup(request));
                type == InteractionType.Redirect && Resolve(this._client.logoutRedirect(request));
            });

        execution
            .then(() => afterCallback && afterCallback());
    }

    GetProfileData = (callback: (response: AxiosResponse<any, any>) => void): void => {

        this.GetAuthentication(auth => {
            const config: AxiosRequestConfig = {},
                headers: AxiosRequestHeaders = new AxiosHeaders;

            headers.setAuthorization(`Bearer ${auth.accessToken}`, true);
            config.headers = headers;

            _httpService.Get(ApiPath().GetAll as string, config, (response?: AxiosResponse<any, any>) => response && response.data && callback && callback(response));
        })
    }

    GetMSGraphData = (Callback: (response: AxiosResponse<any, any>) => void): void => {
        this.GetAuthentication(auth => {
            const config: AxiosRequestConfig = {},
                headers: AxiosRequestHeaders = new AxiosHeaders,
                filter: String[] = [],
                urlParams: string = filter != null && filter.some(x => x) ? `?$select=${filter.join()}` : "",
                url: string = `${process.env.REACT_APP_GRAPH_ME_ENDPOINT}${urlParams}`;

            headers.setAuthorization(`Bearer ${auth.accessToken}`, true);
            config.headers = headers;

            _httpService.Get(url, config, (response?: AxiosResponse<any, any>) => {
                response && response.data && Callback && Callback(response);
            })
        })
    }

    GetAccountInfo = (): AccountInfo | null => {

        let acc = this._client.getActiveAccount();

        if (!acc && this._ctx.accounts && this._ctx.accounts.length > 0)
            acc = this._ctx.accounts[0];

        return acc;
    }

    GetToken = (updateAdToken?: (token?: string) => void, errorCallback?: (error?: unknown) => void): void =>
        this.GetAuthentication(auth => auth && auth.accessToken != "" && updateAdToken && updateAdToken(auth.accessToken), errorCallback);

    Authenticate = (setAuth: React.Dispatch<React.SetStateAction<AuthenticationResult | null>>): void =>
        this.GetAuthentication((auth) => auth && setAuth && setAuth(auth));

    GetAuthentication = (successCallback?: (auth: AuthenticationResult) => void, errorCallback?: (error?: unknown) => void): void => {
        const account = this.GetAccountInfo();
        if (account) {
            const accounts: AccountInfo[] = this._ctx.accounts,
                request: SilentRequest = { ...loginRequest, account: account };

            this._client.acquireTokenSilent(request)
                .then((response: AuthenticationResult) => response && successCallback && successCallback(response))
                .catch(error => { errorCallback && errorCallback(error) });
        }
    }

    SSO = async (): Promise<AuthenticationResult> => {
        const account: AccountInfo = this.GetAccountInfo() as AccountInfo,
            request: SsoSilentRequest = { ...loginRequest, account: account },
            response: AuthenticationResult = await this._client.ssoSilent(request);

        return response;
    }

    Navigate = (): void => {
        const navigate = useNavigate();
        const navigationClient = new CustomNavigationClient(navigate);
        this._client.setNavigationClient(navigationClient);
    };
}